// @flow

import React, {Component} from 'react';
import {connector} from '../store';
import {Link} from 'react-router-dom';
import type {Action} from '../actions/actions';
import {getBasicVideoTrackingLabel, getVideoTrackingLabel, videoDataToPlayerData} from '../helpers';
import Clip from './Clip.jsx';

import debounce from 'lodash.debounce';
import ga from '../../../vendor/google-analytics';

let widths = [1201, 769, 641, 0];
let sizes = [5, 4, 3, 2];
let sensitivity = 40;

function gcd(a, b) {
  if (!b) {
    return a;
  }

  return gcd(b, a % b);
}

export class ClipCarousel extends Component<*, *> {
  trackingCategory: string;
  trackingLabel: ?string;

  constructor(props: *) {
    super(props);
    this.state = {
      touchXStart: null
    };
  }

  resizeHandler: () => void;

  componentDidMount() {

    // Once mounted, bind the window resize handler
    this.resizeHandler = this.pageSizeHandler.bind(this);
    window.addEventListener('resize', debounce(this.resizeHandler, 250));

    // Bind a handler to prevent screen shaking on mac
    document && document.body && document.body.addEventListener('wheel', this.shakeHandler);

    this.trackingCategory = 'VideoPlayer | ClipCarousel';
    this.trackingLabel = getBasicVideoTrackingLabel(this.props.show, this.props.player);

    if (this.trackingLabel) {
      ga('send', 'event', this.trackingCategory, 'Open | [P]', this.trackingLabel);
    }
  }

  componentWillUnmount() {

    // On unmount, unbind the window resize handler
    window.removeEventListener('resize', this.resizeHandler);

    // Unbind the screen shake handler
    document && document.body && document.body.removeEventListener('wheel', this.shakeHandler);

    if (this.trackingLabel) {
      ga('send', 'event', 'VideoPlayer | ClipCarousel', 'Close | [P]', this.trackingLabel);
    }
  }

  shakeHandler(event: WheelEvent) {
    Math.abs(event.deltaX) >= Math.abs(event.deltaY) && event.preventDefault()
  }

  pageSizeHandler() {

    // Make sure the document is available
    if (document.documentElement) {

      // Get the window width
      let width = document.documentElement.clientWidth;

      // Determine the possible window sizes
      let sKeys = widths.filter(w => width > w);

      // Select the largest and get the associated page size
      let newPageSize = sizes[widths.length - sKeys.length];

      // If the new page size is different than the existing size then update
      if (this.props.player.clipBarState.pageSize !== newPageSize) {
        this.props.changeClipPageSize(newPageSize);
      }
    }
  }

  paginationEndHandler(page: number) {
    let action = this.props.changeClipPage;

    return function(event: SyntheticTransitionEvent<*>) {

      // Check to make sure that any bubbling events are ignored
      if (event.target === event.currentTarget) {
        action(page);
      }
    };
  }

  startTouch(event: SyntheticTouchEvent<*>) {

    // If there are no touch events, fail out
    if (!event.touches || event.touches.length < 1) {
      return;
    }

    this.setState({
      touchXStart: event.touches[0].clientX
    });
  }

  touchDrag(left: () => ?Action, right: () => ?Action) {
    return function(event: SyntheticTouchEvent<*>) {

      // If there are no touch events, fail out
      if (!event.touches || event.touches.length < 1) {
        return;
      }

      // If there is no starting x coordinate, fail out
      if (this.state.touchXStart === null) {
        return;
      }

      // Listen for any point in a touch drag that should trigger the action
      if ((event.touches[0].clientX - this.state.touchXStart) > sensitivity) {
        if (this.trackingLabel) {
          ga('send', 'event', this.trackingCategory, 'Move Left | Swipe | [P]', this.trackingLabel);
        }

        left();
      } else if ((event.touches[0].clientX - this.state.touchXStart) < -sensitivity) {
        if (this.trackingLabel) {
          ga('send', 'event', this.trackingCategory, 'Move Right | Swipe | [P]', this.trackingLabel);
        }

        right();
      }
    }.bind(this);
  }

  render() {

    // Create shorthand access to relevant state
    let playingId = this.props.player.currentlyPlaying.id,
        barState = this.props.player.clipBarState,
        clips = this.props.player.container.clips,
        clipAction = this.props.clipAction;

    let pages = clips.length / gcd(barState.pageSize, clips.length),
        containerClass = 'clip-container',
        containerHandler = () => {},
        hasOverflowClips = clips.length > barState.pageSize,
        prevHandler = !barState.animating ? this.props.clickClipLArrow : () => {},
        nextHandler = !barState.animating ? this.props.clickClipRArrow : () => {},
        wheelLeft = () => {
          if (!barState.animating && this.trackingLabel) {
            ga('send', 'event', this.trackingCategory, 'Move Left | Wheel | [P]', this.trackingLabel);
          }

          prevHandler();
        },
        wheelRight = () => {
          if (!barState.animating && this.trackingLabel) {
            ga('send', 'event', this.trackingCategory, 'Move Right | Wheel | [P]', this.trackingLabel);
          }

          nextHandler();
        },
        wheelHandler = hasOverflowClips ? (e) => e.deltaX < -sensitivity && wheelLeft() || e.deltaX > sensitivity && wheelRight() : () => {};

    // If there are more clips than a page size, then pagination is needed.
    // To ensure there are enough clips for pagination, generate a list with
    // three full pages worth of clips
    if (hasOverflowClips) {

      // Determine the start location of the focus
      let focusStart = (barState.currentPage - 1) * barState.pageSize % clips.length;
      let focusEnd = focusStart + barState.pageSize;

      // Compute the area of the clips that will be visible on the current page
      let focus = clips.slice(focusStart, focusStart + barState.pageSize);

      // For clip lengths that do not align with the pages size, additional
      // clips may be needed to pad out the focus area
      let focusPadding = Math.max(0, barState.pageSize - focus.length);

      // If the focus needs to be extended, add additional clips
      if (focusPadding > 0) {
        focus = focus.concat(clips.slice(0, focusPadding));

        // If the focus area wrapped, then adjust the ending point
        focusEnd = focusPadding;
      }

      // Compute the list that follows the focus
      let tail = clips.slice(focusEnd, focusEnd + barState.pageSize);

      // If the tail is too short, pad from the start of the list
      if (tail.length < barState.pageSize) {
        tail = tail.concat(clips.slice(0, barState.pageSize - tail.length));
      }

      // Compute the list that precedes the focus
      let head = clips.slice(Math.max(0, focusStart - barState.pageSize), focusStart);

      // If the head is too short, add clips from the end of the list
      if (head.length < barState.pageSize) {
        head = clips.slice(-1 * (barState.pageSize - head.length)).concat(head);
      }

      // Finally combine the lists to create the displayable clip list
      clips = head.concat(focus).concat(tail);

      // Mark the container as an overflowing container
      containerClass += ' overflow';
    }

    // If the bar is in an animating state, then handle adding the animation
    // styling, and post animation updates
    if (barState.animating) {
      containerClass += ' animating';
      containerClass += ' animating-' + barState.animDirection.toLowerCase();

      // Compute the next page that should be shown
      let newPage = barState.animDirection === 'RIGHT' ? barState.currentPage + 1 : barState.currentPage - 1;

      // Perform bounds checks to make sure the next page is valid. If it is
      // not then the new page value should wrap
      if (newPage > pages) {
        newPage = 1;
      } else if (newPage < 1) {
        newPage = pages;
      }

      // Create the transition end handler based on the page that is being
      // animating to
      containerHandler = this.paginationEndHandler(newPage);
    }

    // Add in the page size to clip container
    containerClass += ' page-size-' + barState.pageSize;

    return (
      <div className={"clipbar clipbar-carousel" + (hasOverflowClips ? ' overflow' : '')}>
        {hasOverflowClips &&
          <div className="clipbar-control clipbar-left fas fa-angle-left" onClick={() => {
            if (this.trackingLabel) {
              ga('send', 'event', this.trackingCategory, 'Move Left | Arrow | [P]', this.trackingLabel);
            }

            prevHandler();
          }}/>
        }
        <div className="clip-window">
          <div
            className={containerClass}
            onTouchStart={hasOverflowClips ? this.startTouch.bind(this) : () => {}}
            onTouchMove={hasOverflowClips ? this.touchDrag(prevHandler, nextHandler) : () => {}}
            onWheel={wheelHandler}
            onTransitionEnd={containerHandler}
          >
            {clips.map(function(clip, i) {
              let isPlaying = clip.id === playingId,
                  handler = () => {
                    let trackingLabel = getVideoTrackingLabel(this.props.show, this.props.player, clip.id);

                    if (trackingLabel) {
                      ga('send', 'event', this.trackingCategory, 'Click Clip | [A]', trackingLabel + ' | ' + (i + 1 - barState.pageSize));
                    }

                    clipAction(videoDataToPlayerData(clip, true));
                  };

              let path = `/${this.props.show.slug}/video/${clip.slug}/`;
              let replace = path === window.location.pathname;

              return <Link className="no-delay" key={clip.id + '.' + i} to={path} replace={replace}>
                <Clip {...clip} isPlaying={isPlaying} handler={handler} />
              </Link>;

            }.bind(this))}
          </div>
        </div>
        {hasOverflowClips &&
          <div className="clipbar-control clipbar-right fas fa-angle-right" onClick={
            () => {
              if (this.trackingLabel) {
                ga('send', 'event', this.trackingCategory, 'Move Right | Arrow | [P]', this.trackingLabel);
              }

              nextHandler();
            }}/>
        }
      </div>
    );
  }
}

export default connector(ClipCarousel);